/**
 * ticket-discount — lignes « Sous-total » / « Remise X % » d'un ticket de caisse.
 *
 * La remise globale vit dans sales.discount_percent ; les lignes gardent leur prix (sale_items.unit_price) et
 * sales.total porte le montant payé. Sans ces deux lignes, le ticket montrait des articles dont la somme ne
 * faisait pas le total (cas Vallon PV-9 : 333,00 € d'articles, 283,05 € payés, aucune ligne Remise).
 *
 * Le montant de la remise est l'écart réel entre les lignes et le total payé : le ticket tombe juste au centime,
 * quelle que soit la façon d'arrondir de la caisse. Module pur (aucune dépendance), partagé par tous les rendus.
 */

import type { TicketData } from './escpos-types';

export type TicketAmountRow = { label: string; amount: number };

const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

/** « 15 » → « 15 », « 12.5 » → « 12,5 » (affichage français, sans zéros inutiles). */
const formatPercent = (p: number): string => String(round2(p)).replace('.', ',');

/**
 * Lignes à imprimer entre les articles et les totaux : [] sans remise globale (ou masquée par le commerce via
 * ticket_config.show_remise_globale = false), sinon [Sous-total, Remise -X].
 */
export function globalDiscountRows(
  data: Pick<TicketData, 'items' | 'total_ttc' | 'discount_percent' | 'config'>,
): TicketAmountRow[] {
  const percent = Number(data.discount_percent) || 0;
  if (percent <= 0 || data.config.show_remise_globale === false) return [];
  const gross = round2(data.items.reduce((s, i) => s + round2(i.quantity * i.unit_price), 0));
  const discount = round2(gross - data.total_ttc);
  if (discount <= 0) return [];
  return [
    { label: 'Sous-total', amount: gross },
    { label: `Remise ${formatPercent(percent)} %`, amount: -discount },
  ];
}

/** Montant signé au format ticket thermique : « -49.95 EUR ». */
export const formatTicketAmount = (amount: number): string => amount.toFixed(2) + ' EUR';
